import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, BookOpen, Menu, X, Info } from 'lucide-react';
import { buildVerticalPath, getVerticalBranding, type VerticalType } from '../config/vertical';

interface PublicHeaderProps {
  /** Optional custom right-side content (e.g. Close Preview button). When set, default nav and hamburger are hidden. */
  rightContent?: React.ReactNode;
  vertical?: VerticalType;
  brandName?: string;
  logoSrc?: string;
}

/** Brown wood-texture header for all unauthenticated pages (Landing, Login, Blog, Resources, etc.). */
const PublicHeader: React.FC<PublicHeaderProps> = ({
  rightContent,
  vertical = 'vineyard',
  brandName,
  logoSrc,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const defaults = getVerticalBranding(vertical);
  const activeBrandName = brandName || defaults.brandName;
  const activeLogoSrc = logoSrc || defaults.logoSrc;
  const showDefaultNav = rightContent === undefined;
  const isVineyard = vertical === 'vineyard';
  const isOrchard = vertical === 'orchard';
  const isGreenhouse = vertical === 'greenhouse';
  const headerClass = isVineyard
    ? 'wood-texture border-wood-950'
    : isOrchard
      ? 'bg-gradient-to-r from-blue-900 to-blue-700 border-blue-950'
      : isGreenhouse
        ? 'bg-green-50 border-green-200'
        : 'bg-gradient-to-r from-green-900 to-green-700 border-green-950';
  const navHoverClass = isVineyard ? 'hover:bg-wood-800' : isOrchard ? 'hover:bg-blue-800' : isGreenhouse ? 'hover:bg-green-100' : 'hover:bg-green-800';
  const navLinkClass = isGreenhouse ? 'text-wood-800 hover:text-wood-900' : 'text-vineyard-white hover:text-wood-100';
  const signInButtonClass = isVineyard
    ? 'bg-gradient-to-br from-grape-400 to-grape-600 hover:from-grape-500 hover:to-grape-700'
    : isOrchard
      ? 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700'
      : isGreenhouse
        ? 'border-2 border-green-600 text-green-700 hover:bg-green-50 bg-white'
        : 'bg-gradient-to-br from-green-400 to-green-600 hover:from-green-500 hover:to-green-700';
  const getStartedButtonClass = isVineyard
    ? 'bg-gradient-to-br from-leaf-400 to-leaf-600 hover:from-leaf-500 hover:to-leaf-700'
    : isOrchard
      ? 'bg-gradient-to-br from-sky-300 to-sky-500 hover:from-sky-400 hover:to-sky-600'
      : isGreenhouse
        ? 'bg-green-600 hover:bg-green-700 text-white'
        : 'bg-gradient-to-br from-lime-400 to-green-500 hover:from-lime-500 hover:to-green-600';
  const brandTextClass = isGreenhouse ? 'text-wood-900' : 'text-vineyard-white';

  return (
    <header className={`sticky top-0 z-50 shadow-lg border-b-2 relative ${headerClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 sm:py-4 gap-3">
          <Link to={buildVerticalPath(vertical, '')} className="flex items-center group flex-shrink-0 min-w-0">
            <img
              src={activeLogoSrc}
              alt={`${activeBrandName} Logo`}
              className="h-8 w-auto sm:h-11 sm:w-auto object-contain transition-transform group-hover:scale-105 flex-shrink-0"
            />
            <span className={`font-brand ml-2 sm:ml-3 text-lg sm:text-2xl font-bold whitespace-nowrap ${brandTextClass}`}>
              {activeBrandName}
            </span>
          </Link>

          {showDefaultNav ? (
            <>
              <nav className="hidden md:flex items-center flex-shrink-0 space-x-2 lg:space-x-4">
                <Link
                  to={buildVerticalPath(vertical, '/resources')}
                  className={`${navLinkClass} px-3 py-2 text-sm font-medium flex items-center transition-all duration-200 rounded ${navHoverClass}`}
                >
                  <FileCheck className="h-4 w-4 mr-1.5" />
                  Resources
                </Link>
                <Link
                  to={buildVerticalPath(vertical, '/blog')}
                  className={`${navLinkClass} px-3 py-2 text-sm font-medium flex items-center transition-all duration-200 rounded ${navHoverClass}`}
                >
                  <BookOpen className="h-4 w-4 mr-1.5" />
                  Blog
                </Link>
                <Link
                  to={buildVerticalPath(vertical, '/about')}
                  className={`${navLinkClass} px-3 py-2 text-sm font-medium flex items-center transition-all duration-200 rounded ${navHoverClass}`}
                >
                  <Info className="h-4 w-4 mr-1.5" />
                  About
                </Link>
                <Link
                  to={buildVerticalPath(vertical, '/login')}
                  className={`${signInButtonClass} ${!isGreenhouse ? 'text-vineyard-white' : ''} px-4 py-2 rounded text-sm font-semibold transition-all duration-300 shadow hover:shadow-lg`}
                >
                  Sign in
                </Link>
                <Link
                  to={buildVerticalPath(vertical, '/signup')}
                  className={`${getStartedButtonClass} ${!isGreenhouse ? 'text-vineyard-white' : ''} px-4 py-2 rounded text-sm font-semibold transition-all duration-300 shadow hover:shadow-lg`}
                >
                  Get Started
                </Link>
              </nav>
              <button
                type="button"
                className={`md:hidden flex items-center justify-center p-2 rounded transition-colors flex-shrink-0 ${isGreenhouse ? 'text-wood-800 hover:bg-green-100' : 'text-vineyard-white'} ${navHoverClass}`}
                onClick={() => setMobileMenuOpen((open) => !open)}
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </>
          ) : (
            <div className="flex items-center flex-shrink-0">{rightContent}</div>
          )}
        </div>
      </div>

      {showDefaultNav && mobileMenuOpen && (
        <div className={`md:hidden absolute top-full left-0 right-0 z-40 border-b-2 shadow-lg ${headerClass}`}>
          <nav className="flex flex-col py-2 px-4 max-w-7xl mx-auto">
            <Link
              to={buildVerticalPath(vertical, '/resources')}
              className={`${navLinkClass} py-3 px-4 rounded flex items-center text-sm font-medium ${navHoverClass}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <FileCheck className="h-4 w-4 mr-3" />
              Resources
            </Link>
            <Link
              to={buildVerticalPath(vertical, '/blog')}
              className={`${navLinkClass} py-3 px-4 rounded flex items-center text-sm font-medium ${navHoverClass}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <BookOpen className="h-4 w-4 mr-3" />
              Blog
            </Link>
            <Link
              to={buildVerticalPath(vertical, '/about')}
              className={`${navLinkClass} py-3 px-4 rounded flex items-center text-sm font-medium ${navHoverClass}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Info className="h-4 w-4 mr-3" />
              About
            </Link>
            <Link
              to={buildVerticalPath(vertical, '/login')}
              className={`${signInButtonClass} ${!isGreenhouse ? 'text-vineyard-white' : ''} py-3 px-4 rounded text-sm font-semibold text-center mt-1 shadow hover:shadow-lg transition-all duration-300`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Sign in
            </Link>
            <Link
              to={buildVerticalPath(vertical, '/signup')}
              className={`${getStartedButtonClass} ${!isGreenhouse ? 'text-vineyard-white' : ''} py-3 px-4 rounded text-sm font-semibold text-center mt-1 shadow hover:shadow-lg transition-all duration-300`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Get Started
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
